import { DEFAULT_CHATBOT_TYPE, type ChatbotType } from '../model/ChatbotType';

// --- Request Types ---

export interface SendMessageRequest {
  gorgiasCustomerId: string;
  email?: string;
  gorgiasTicketId: string;
  message: string;
  fromAgent?: boolean;
  channel?: string;
}

export interface ResolveActionRequest {
  conversationId: string;
  approved: boolean;
  reason?: string;
}

export interface CreateDocumentRequest {
  source: string;
  title: string;
  content: string;
  language?: string;
}

export interface UpdateDocumentRequest {
  title?: string;
  content?: string;
  language?: string;
  source?: string;
}

// --- Response Types ---

export interface ConversationMessage {
  role: string;
  source: string;
  body: string;
  createdAt: string;
}

export interface ToolCallLog {
  name: string;
  args: Record<string, unknown>;
  result: string;
  durationMs: number;
}

export interface SendMessageResponse {
  shouldInvokeAi: boolean;
  userId?: string;
  conversationId?: string;
  gorgiasTicketId?: string;
  lastMessage?: string;
  messages: ConversationMessage[];
  toolCalls?: ToolCallLog[];
  latencyMs?: number;
}

// --- Scenario Types ---

export interface SaveScenarioRequest {
  name: string;
  config: any;
  messages: any[];
}

export interface ScenarioSummary {
  scenarioId: string;
  name: string;
  messageCount: number;
  createdAt: string;
}

export interface ListScenariosResponse {
  scenarios: ScenarioSummary[];
}

// --- Rating Types ---

export interface SaveRatingRequest {
  conversationId: string;
  messageIndex: number;
  rating: number;
  comment?: string;
}

export interface RatingEntry {
  ratingId: string;
  messageIndex: number;
  rating: number;
  comment?: string;
  createdAt: string;
}

export interface GetRatingsResponse {
  ratings: RatingEntry[];
}

export interface GetConversationResponse {
  found: boolean;
  conversation?: Record<string, unknown>;
  messages?: ConversationMessage[];
  pendingActions?: Array<Record<string, unknown>>;
}

export interface KnowledgeBaseDocument {
  documentId: string;
  title: string;
  source: string;
  language?: string;
  content?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ListDocumentsResponse {
  documents: KnowledgeBaseDocument[];
  total: number;
}

export interface GetDocumentResponse {
  found: boolean;
  document?: KnowledgeBaseDocument;
  chunks?: Array<Record<string, unknown>>;
}

export interface SearchResult {
  chunk: Record<string, unknown>;
  document: {
    documentId: string;
    title: string;
    source: string;
    language?: string;
  };
}

export interface SearchResponse {
  query: string;
  total: number;
  results: SearchResult[];
}

export class CsAgentClient {
  private readonly headers: HeadersInit = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  constructor(private readonly apiUrl: string) {}

  // ─── CS Agent Playground ───────────────────────────────────────────

  public async sendMessage(body: SendMessageRequest, signal?: AbortSignal): Promise<SendMessageResponse> {
    try {
      const response = await fetch(`${this.apiUrl}/csAgent/sendMessage`, {
        method: 'POST',
        headers: this.headers,
        mode: 'cors',
        body: JSON.stringify(body),
        signal: signal ?? null,
      });

      if (!response.ok) {
        throw new Error(`Failed to send message: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  public async getConversation(gorgiasTicketId: string): Promise<GetConversationResponse> {
    try {
      const response = await fetch(`${this.apiUrl}/csAgent/getConversation/${gorgiasTicketId}`, {
        method: 'GET',
        headers: this.headers,
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`Failed to get conversation: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error getting conversation:', error);
      throw error;
    }
  }

  public async getUser(gorgiasCustomerId: string): Promise<any> {
    try {
      const response = await fetch(`${this.apiUrl}/csAgent/getUser/${gorgiasCustomerId}`, {
        method: 'GET',
        headers: this.headers,
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`Failed to get user: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error getting user:', error);
      throw error;
    }
  }

  public async resolveAction(actionId: string, body: ResolveActionRequest): Promise<any> {
    try {
      const response = await fetch(`${this.apiUrl}/csAgent/resolveAction/${actionId}`, {
        method: 'POST',
        headers: this.headers,
        mode: 'cors',
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`Failed to resolve action: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error resolving action:', error);
      throw error;
    }
  }

  // ─── Knowledge Base ────────────────────────────────────────────────

  public async listDocuments(
    source?: string,
    type: ChatbotType = DEFAULT_CHATBOT_TYPE,
  ): Promise<ListDocumentsResponse> {
    try {
      const query = this.buildQuery({ source, type });
      const response = await fetch(`${this.apiUrl}/csAgent/knowledgeBase${query}`, {
        method: 'GET',
        headers: this.headers,
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`Failed to list documents: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error listing documents:', error);
      throw error;
    }
  }

  public async getDocument(documentId: string, type: ChatbotType = DEFAULT_CHATBOT_TYPE): Promise<GetDocumentResponse> {
    try {
      const query = this.buildQuery({ type });
      const response = await fetch(`${this.apiUrl}/csAgent/knowledgeBase/${documentId}${query}`, {
        method: 'GET',
        headers: this.headers,
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`Failed to get document: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error getting document:', error);
      throw error;
    }
  }

  public async createDocument(body: CreateDocumentRequest, type: ChatbotType = DEFAULT_CHATBOT_TYPE): Promise<any> {
    try {
      const query = this.buildQuery({ type });
      const response = await fetch(`${this.apiUrl}/csAgent/knowledgeBase${query}`, {
        method: 'POST',
        headers: this.headers,
        mode: 'cors',
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`Failed to create document: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error creating document:', error);
      throw error;
    }
  }

  public async updateDocument(
    documentId: string,
    body: UpdateDocumentRequest,
    type: ChatbotType = DEFAULT_CHATBOT_TYPE,
  ): Promise<any> {
    try {
      const query = this.buildQuery({ type });
      const response = await fetch(`${this.apiUrl}/csAgent/knowledgeBase/${documentId}${query}`, {
        method: 'PUT',
        headers: this.headers,
        mode: 'cors',
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`Failed to update document: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error updating document:', error);
      throw error;
    }
  }

  public async deleteDocument(documentId: string, type: ChatbotType = DEFAULT_CHATBOT_TYPE): Promise<any> {
    try {
      const query = this.buildQuery({ type });
      const response = await fetch(`${this.apiUrl}/csAgent/knowledgeBase/${documentId}${query}`, {
        method: 'DELETE',
        headers: this.headers,
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`Failed to delete document: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  }

  public async searchKnowledgeBase(
    query: string,
    limit?: number,
    type: ChatbotType = DEFAULT_CHATBOT_TYPE,
  ): Promise<SearchResponse> {
    try {
      const params = this.buildQuery({ query, type, limit });
      const response = await fetch(`${this.apiUrl}/csAgent/knowledgeBase/search${params}`, {
        method: 'GET',
        headers: this.headers,
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`Failed to search knowledge base: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error searching knowledge base:', error);
      throw error;
    }
  }

  // ─── Scenarios ────────────────────────────────────────────────────

  public async saveScenario(body: SaveScenarioRequest): Promise<any> {
    try {
      const response = await fetch(`${this.apiUrl}/csAgent/scenarios`, {
        method: 'POST',
        headers: this.headers,
        mode: 'cors',
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`Failed to save scenario: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error saving scenario:', error);
      throw error;
    }
  }

  public async listScenarios(): Promise<ListScenariosResponse> {
    try {
      const response = await fetch(`${this.apiUrl}/csAgent/scenarios`, {
        method: 'GET',
        headers: this.headers,
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`Failed to list scenarios: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error listing scenarios:', error);
      throw error;
    }
  }

  public async getScenario(scenarioId: string): Promise<any> {
    try {
      const response = await fetch(`${this.apiUrl}/csAgent/scenarios/${scenarioId}`, {
        method: 'GET',
        headers: this.headers,
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`Failed to get scenario: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error getting scenario:', error);
      throw error;
    }
  }

  public async deleteScenario(scenarioId: string): Promise<any> {
    try {
      const response = await fetch(`${this.apiUrl}/csAgent/scenarios/delete/${scenarioId}`, {
        method: 'POST',
        headers: this.headers,
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`Failed to delete scenario: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error deleting scenario:', error);
      throw error;
    }
  }

  // ─── Ratings ──────────────────────────────────────────────────────

  public async saveRating(body: SaveRatingRequest): Promise<any> {
    try {
      const response = await fetch(`${this.apiUrl}/csAgent/ratings`, {
        method: 'POST',
        headers: this.headers,
        mode: 'cors',
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`Failed to save rating: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error saving rating:', error);
      throw error;
    }
  }

  public async getRatings(conversationId: string): Promise<GetRatingsResponse> {
    try {
      const response = await fetch(`${this.apiUrl}/csAgent/ratings/${conversationId}`, {
        method: 'GET',
        headers: this.headers,
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`Failed to get ratings: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error getting ratings:', error);
      throw error;
    }
  }

  // ─── System Prompt ──────────────────────────────────────────────────

  public async getPrompt(
    type: ChatbotType = DEFAULT_CHATBOT_TYPE,
  ): Promise<{ prompt: string; version: number; isDefault: boolean }> {
    const query = this.buildQuery({ type });
    const response = await fetch(`${this.apiUrl}/csAgent/prompt${query}`, {
      headers: this.headers,
      mode: 'cors',
    });
    if (!response.ok) {
      throw new Error(`Failed to get prompt: ${response.status}`);
    }
    return response.json();
  }

  public async savePrompt(
    prompt: string,
    type: ChatbotType = DEFAULT_CHATBOT_TYPE,
  ): Promise<{ success: boolean; version: number }> {
    const query = this.buildQuery({ type });
    const response = await fetch(`${this.apiUrl}/csAgent/prompt${query}`, {
      method: 'POST',
      headers: this.headers,
      mode: 'cors',
      body: JSON.stringify({ prompt }),
    });
    if (!response.ok) {
      throw new Error(`Failed to save prompt: ${response.status}`);
    }
    return response.json();
  }

  public async resetPrompt(
    type: ChatbotType = DEFAULT_CHATBOT_TYPE,
  ): Promise<{ success: boolean; prompt: string; version: number }> {
    const query = this.buildQuery({ type });
    const response = await fetch(`${this.apiUrl}/csAgent/prompt/reset${query}`, {
      method: 'POST',
      headers: this.headers,
      mode: 'cors',
    });
    if (!response.ok) {
      throw new Error(`Failed to reset prompt: ${response.status}`);
    }
    return response.json();
  }

  private buildQuery(params: Record<string, string | number | undefined>): string {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        searchParams.set(key, String(value));
      }
    });
    const query = searchParams.toString();
    return query ? `?${query}` : '';
  }
}
